import { FC, HTMLAttributes, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button, Checkbox, Flex, Form, Input, notification, Typography } from 'antd';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { useAddVerificationMutation, useSourceSlice } from 'entities/Source';
import { EVerificationTypes, ISource } from 'entities/Source/model/types';
import { EOnboardingStates, EResultTypes, useOnboardingSlice } from 'features/Onboarding';
import { EInternalEmails, EMainRoutes, EYmEvents, EYmEventsO1, EYmEventsO2 } from 'shared/typings';
import { CopyButton, RadioGroup } from 'shared/ui';
import { sendYmEvent } from 'shared/utils';
const { Title, Text } = Typography;

interface IVerificationFormProps extends HTMLAttributes<HTMLDivElement> {
	source?: ISource;
	isOnboarding?: boolean;
	isAdding?: boolean;
}

export const VerificationForm: FC<IVerificationFormProps> = ({
	className,
	source,
	isOnboarding,
	isAdding,
	...props
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { clearValues } = useSourceSlice();
	const { createdSource, setResultType, setModalState } = useOnboardingSlice();
	const openFromVerify = !isAdding && !isOnboarding;

	const [addVerification, { isLoading, isSuccess }] = useAddVerificationMutation();

	const initialValues = { type: '' as EVerificationTypes, agreement: false };

	const validationSchema = Yup.object({
		type: Yup.string().required(t('Необходимо выбрать тип верификации')),
		agreement: Yup.bool().isTrue(t('Необходимо согласиться с инструкциями')),
	});

	const createdUuid = createdSource?.uuid || source?.uuid || '';
	const createdName = createdSource?.name || source?.name || '';
	const metaTag = `<meta name="Affilead-verification" content="${createdUuid}" />`;

	useEffect(() => {
		if (!isSuccess) return;

		clearValues();

		if (isOnboarding) {
			setResultType(EResultTypes.SUCCESS);
			setModalState(EOnboardingStates.FINAL);
		} else {
			navigate(`/${EMainRoutes.SOURCES}`);
			notification.open({
				message: openFromVerify ? t('Заявка на верификацию отправлена успешно') : t('Площадка создана успешно'),
				description: t(
					'После подтверждения владения {{sourceName}}, последняя автоматически попадет на модерацию и после ее прохождения можно подавать заявки на подключение к офферам.',
					{ sourceName: createdName },
				),
				type: 'success',
				placement: 'topRight',
				showProgress: true,
				pauseOnHover: true,
			});
		}
	}, [isSuccess]); // eslint-disable-line

	const onSkip = () => {
		clearValues();

		if (isOnboarding) {
			sendYmEvent({
				event: EYmEvents.CLICK_SLIP_VERIFY_ADSPACE,
				o1: [
					EYmEventsO1.ACCOUNT,
					EYmEventsO1.PUBLISHER,
					isOnboarding ? EYmEventsO1.WELCOME_POPUP : EYmEventsO1.ADSPACE,
				],
				o2: isOnboarding ? EYmEventsO2.ADD_ADSPACE : [EYmEventsO2.ADD_ADSPACE, EYmEventsO2.VERIFY],
				wi: createdUuid,
			});
			setResultType(EResultTypes.NEED_VERIFICATION);
			setModalState(EOnboardingStates.FINAL);
		} else {
			navigate(`/${EMainRoutes.SOURCES}`);
			notification.open({
				message: t('Требуется подтвердить владение площадкой'),
				description: t(
					'{{sourceName}} создана успешно, но требуется подтвердить ее владение. Без подтверждения ее владения и прохождения модерации, вы не сможете подавать заявки на подключение к офферам.',
					{ sourceName: createdName },
				),
				type: 'warning',
				placement: 'topRight',
				showProgress: true,
				pauseOnHover: true,
			});
		}
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={(values) => {
				sendYmEvent({
					event: EYmEvents.CLICK_VERIFY_ADSPACE,
					o1: [
						EYmEventsO1.ACCOUNT,
						EYmEventsO1.PUBLISHER,
						isOnboarding ? EYmEventsO1.WELCOME_POPUP : EYmEventsO1.ADSPACE,
					],
					o2: isOnboarding ? EYmEventsO2.ADD_ADSPACE : [EYmEventsO2.ADD_ADSPACE, EYmEventsO2.VERIFY],
					o4: values.type === EVerificationTypes.BY_EMAIL ? 'email' : 'code',
					wi: createdUuid,
				});
				addVerification({ type: values.type, body: { source_pk: createdUuid } });
			}}>
			{({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => {
				return (
					<form onSubmit={handleSubmit}>
						<Flex {...props} vertical gap={24} className={className}>
							<Flex vertical gap={20}>
								<Form.Item
									layout="vertical"
									label={t('Способ верификации')}
									required
									validateStatus={errors.type && touched.type ? 'error' : ''}
									help={errors.type && touched.type && errors.type}>
									<RadioGroup
										items={[
											{ label: t('Подтвердить через код'), value: EVerificationTypes.BY_CODE },
											{ label: t('Подтвердить через email'), value: EVerificationTypes.BY_EMAIL },
										]}
										name="type"
										onChange={handleChange}
										onBlur={handleBlur}
										value={values.type}
										disabled={isLoading}
									/>
								</Form.Item>

								{values.type && values.type === EVerificationTypes.BY_CODE && (
									<Flex vertical gap={16}>
										<Title level={5} style={{ margin: 0 }}>
											{t('1. Вставьте код между тегами <head> </head> на каждой странице вашей площадки')}
										</Title>
										<Flex gap={12}>
											<Input name="url" readOnly value={metaTag} />
											<CopyButton text={metaTag} style={{ minWidth: 40 }} />
										</Flex>
									</Flex>
								)}

								{values.type && values.type === EVerificationTypes.BY_EMAIL && (
									<Flex vertical gap={16}>
										<Title level={5} style={{ margin: 0 }}>
											<Trans i18nKey="emailVerificationtCheckboxTitle">
												1. Отправьте письмо в поддержку{' '}
												<a href={`mailto:${EInternalEmails.SUPPORT}`}>{EInternalEmails.SUPPORT}</a>
											</Trans>
										</Title>
										<Text type="secondary" style={{ fontSize: 16 }}>
											<Trans i18nKey="emailVerificationtCheckboxLabel">
												Отправьте нам письмо на email{' '}
												<a href={`mailto:${EInternalEmails.SUPPORT}`}>{EInternalEmails.SUPPORT}</a> с email совпадающего
												по домену с URL вашей площадки. Пример: если ваш email webmester@affilead.ru, а домен вашей
												площадки - affilead.ru, то @affilead.ru совпадает c affilead.ru.
											</Trans>
										</Text>
									</Flex>
								)}

								{values.type && (
									<Flex vertical gap={16}>
										<Title level={5} style={{ margin: 0 }}>
											{t('2. Отправьте свое подтверждение')}
										</Title>
										<Form.Item
											validateStatus={errors.agreement && touched.agreement ? 'error' : ''}
											help={errors.agreement && touched.agreement && errors.agreement}>
											<Checkbox
												onChange={(e) => setFieldValue('agreement', e.target.checked)}
												name="agreement"
												onBlur={handleBlur}
												checked={values.agreement}
												disabled={isLoading}>
												{t(
													'Я подтверждаю, что следовал приведенным выше инструкциям, чтобы подтвердить владение своей площадкой.',
												)}
											</Checkbox>
										</Form.Item>
									</Flex>
								)}
							</Flex>

							<Flex justify={openFromVerify ? 'space-between' : 'end'} gap={16}>
								{openFromVerify && (
									<Button disabled={isLoading} onClick={() => navigate(`/${EMainRoutes.SOURCES}`)}>
										{t('Отменить')}
									</Button>
								)}

								{!openFromVerify && (
									<Button variant="text" color="primary" disabled={isLoading} onClick={onSkip}>
										{t('Пропустить шаг')}
									</Button>
								)}

								<Button
									type="primary"
									htmlType="submit"
									loading={isLoading}
									disabled={isLoading || !!Object.keys(errors).length || !values.type}>
									{t('Подтвердить')}
								</Button>
							</Flex>
						</Flex>
					</form>
				);
			}}
		</Formik>
	);
};
